import Api from "../api/api";
import {useEffect,useState,useRef} from "react";
import {dateRange, toDateString} from "../../utilities/dateUtilities";
import { reportDatePresetByLabel } from "./reportsEnums";
import {saveAs} from 'file-saver';
import Loader from "../shared/loader";
import {EmptyState} from "../dashboard/emptyState";
import {ReportsCharts} from "./reportsCharts";
import stringHelpers from "../../utilities/stringHelpers";
import arrayUtilities from "../../utilities/arrayUtilities";
import {useLocation} from "react-router-dom/cjs/react-router-dom";
import {FiltersModalTrigger} from "../shared/filtersModalTrigger";
import { AppliedFilterPill, renderTagFilterItemLabel } from "../shared/appliedFilterPill";
import appliedFiltersUtilities from "../../utilities/appliedFiltersUtilities";
import {PcnsIssuedReportFiltersModal} from "./filters-modals/pcnsIssuedReportFiltersModal";
import {Link} from "react-router-dom/cjs/react-router-dom.min";
import { downloadReportPdf } from "./downloadReportPdf";
import { PrintPdfHeader } from "./printPdfHeader";
import { PcnsIssuedScheduleModal } from "./schedule-modals/pcnsIssuedScheduleModal";
const groupByLabel = (value) => value === 1 ? "Tag" : "Site"

export default function PcnsIssuedReport ({organisationId, organisationName, organisationLogo, tags, token}) {
    const location = useLocation()
    const hasDatesFromLocationState = location && location.state !== undefined && (location.state.fromDate !== undefined || location.state.toDate !== undefined)

    const [reportData, setReportData] = useState(null);
    const [fromDate,setFromDate] = useState(location && location.state !== undefined && location.state.fromDate !== undefined ? location.state.fromDate : dateRange("lastMonth").firstDate);
    const [toDate,setToDate] = useState(location && location.state !== undefined && location.state.toDate !== undefined ? location.state.toDate : dateRange("lastMonth").lastDate);
    const [datePreset,setDatePreset] = useState(hasDatesFromLocationState ? null : reportDatePresetByLabel["Last month"]);
    const [pcnsIssuedGroupBy,setPcnsIssuedGroupBy] = useState(0);
    const [selectedTags,setSelectedTags] = useState([]);
    const [isLoading,setIsLoading] = useState(true);
    const [chartStyle,setChartStyle] = useState("bar");
    const [totalCount,setTotalCount] = useState(null);
    const [chartData,setChartData] = useState(null);
    const [showFiltersModal,setShowFiltersModal] = useState(false);
    const [showFilterPills,setShowFilterPills] = useState(false);
    const [appliedFilters,setAppliedFilters] = useState(null);
    const [showScheduleExportModal, setShowScheduleExportModal] = useState(false);
    const reportsLandingUrl = `/organisation/${organisationId}/reports`;
    const pdfRef = useRef()

    const fetchPcnsIssuedReport = (from,to,groupBy,tagIds) => {
        setIsLoading(true)
        Api.getPcnsIssuedReport(reportData => {
            setReportData(reportData.map(item => ({
                    label:groupBy === 1 ? item.tagName : item.siteName,value:item.pcnsIssued,
                })))
            setTotalCount(reportData.reduce((acc,curr) => acc + curr.pcnsIssued,0));
            setIsLoading(false);
        },() => {
            setIsLoading(false)
        }, from, to, 0, groupBy, tagIds, organisationId, token)
    }

    const handleResetFilters = () => {
        const defaultRange = dateRange("lastMonth");
        setFromDate(defaultRange.firstDate);
        setToDate(defaultRange.lastDate);
        setDatePreset(reportDatePresetByLabel["Last month"]);
        setPcnsIssuedGroupBy(0);
        setSelectedTags([]);
        setAppliedFilters({
            "Date Range": `${toDateString(new Date(defaultRange.firstDate))} - ${toDateString(new Date(defaultRange.lastDate))}`,
            "Group By": "Site"
        });
        setShowFilterPills(true);
        fetchPcnsIssuedReport(defaultRange.firstDate,defaultRange.lastDate,0,[]);
    }

    const clearFilterByLabel = (label) => {
        switch (label) {
            case "Date Range": {
                const defaultRange = dateRange("lastMonth")
                setFromDate(defaultRange.firstDate)
                setToDate(defaultRange.lastDate)
                setDatePreset(reportDatePresetByLabel["Last month"])
                fetchPcnsIssuedReport(defaultRange.firstDate,defaultRange.lastDate,pcnsIssuedGroupBy,selectedTags)
                break
            }
            case "Group By":
                setPcnsIssuedGroupBy(0)
                fetchPcnsIssuedReport(fromDate,toDate,0,selectedTags)
                break
            case "Tags":
                setSelectedTags([])
                fetchPcnsIssuedReport(fromDate,toDate,pcnsIssuedGroupBy,[])
                break
            default:
                break
        }
    }

    const handleFilterPillClose = closedLabel => {
        clearFilterByLabel(closedLabel)
        setAppliedFilters(prevState => {
            const updatedFilters = {...prevState}
            if ( closedLabel === "Date Range" ) {
                const defaultRange = dateRange("lastMonth")
                updatedFilters["Date Range"] = `${toDateString(new Date(defaultRange.firstDate))} - ${toDateString(new Date(defaultRange.lastDate))}`
            } else if ( closedLabel === "Group By" ) {
                updatedFilters["Group By"] = "Site"
            } else {
                updatedFilters[closedLabel] = null
            }
            return appliedFiltersUtilities.hasRemainingFilters(updatedFilters) ? updatedFilters : null
        })
    }

    const handleFilterPillItemRemove = (label, remainingIds) => {
        setAppliedFilters(prevState => appliedFiltersUtilities.keepMultiSelectionItems(prevState, label, remainingIds))
        removeMultiSelectFilterItemByLabel(label, remainingIds)
    }

    const removeMultiSelectFilterItemByLabel = (label, remainingIds) => {
        switch (label) {
            case "Tags":
                setSelectedTags(remainingIds)
                fetchPcnsIssuedReport(fromDate,toDate,pcnsIssuedGroupBy,remainingIds)
                break
            default:
                break
        }
    }


    const scheduleDateParameters = datePreset !== null
        ? { reportDatePreset: datePreset }
        : { from: fromDate, to: toDate }

    const scheduleInitialParameters = {
        ...scheduleDateParameters,
        reportGroupBy: pcnsIssuedGroupBy,
        tagIds: selectedTags,
    }

    const closeScheduleExportModal = () => setShowScheduleExportModal(false)
    useEffect(() => {
        fetchPcnsIssuedReport(fromDate, toDate, pcnsIssuedGroupBy, selectedTags);
        setAppliedFilters({
            "Date Range": `${ toDateString(new Date(fromDate)) } - ${ toDateString(new Date(toDate)) }`,
            "Group By": groupByLabel(pcnsIssuedGroupBy)
        });
        setShowFilterPills(true);
    }, [])

    useEffect(() => {
        if ( reportData && reportData.length > 0 ) {
            setChartData(reportData.map(item => ({
                "label":item.label,"value":item.value,
            })));
        }
    },[reportData,chartStyle])

    const handleDownload = (outputFormat) => {
        Api.getPcnsIssuedReport((blob,filename) => {
            saveAs(blob,filename)
        },() => {
            setIsLoading(false)
        }, fromDate, toDate, outputFormat, pcnsIssuedGroupBy, selectedTags, organisationId, token)
    }

    const groupingColumnHeading = pcnsIssuedGroupBy === 1 ? "Tag" : "Site Name"

    const selectedTagNames = !arrayUtilities.isNullOrEmpty(selectedTags) && !arrayUtilities.isNullOrEmpty(tags)
        ? selectedTags
            .map(selectedTagId => tags.find(tag => tag.id === selectedTagId))
            .filter(tag => tag !== undefined)
            .map(tag => tag.name)
            .join(", ")
        : "All"

    return (
        <>
            <div className="container-fluid">
                <div className="row">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-header">
                                <div className="d-flex align-items-center justify-content-between">
                                    <div>
                                        <h5 className="mb-0">
                                            PCNs Issued
                                        </h5>
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <div className="d-flex mr-2 align-items-center">
                                                <FiltersModalTrigger onClick={ () => setShowFiltersModal(true) } />
                                            </div>
                                        <button
                                            onClick={ () => handleDownload(1) }
                                            className="btn btn-sm btn-default text-white bg-dark nowrap mr-2"
                                        >
                                            Download CSV<i className="ml-2 fas fa-file-excel"></i>
                                        </button>
                                        <button
                                            onClick={ () => downloadReportPdf(pdfRef, `${ organisationName } - PCNs Issued`) }
                                            className="btn btn-sm btn-default nowrap mr-2 text-white" style={ { backgroundColor: "#4A7C8E" } }
                                        >
                                            Download PDF<i className="ml-2 far fa-file-pdf"></i>
                                        </button>
                                        <button
                                            onClick={ () => setShowScheduleExportModal(true) }
                                            className="btn btn-sm btn-secondary nowrap mr-2"
                                        >
                                            Schedule Report<i className="ml-2 far fa-clock"></i>
                                        </button>
                                        <Link
                                            className="btn btn-falcon-default btn-sm mr-3"
                                            type="button"
                                            to={ reportsLandingUrl }>
                                            <i className="fas fa-reply"></i> Back
                                        </Link>
                                    </div>
                                </div>
                            </div>

                            <div className="card-body border-top d-flex flex-column">
                                {
                                    showFilterPills && appliedFilters &&
                                    <div className="d-flex align-items-center flex-wrap">
                                        {
                                            Object.entries(appliedFilters).map(
                                                ([label,value]) => {
                                                    const resettable = label === "Date Range" || label === "Group By"
                                                    return <AppliedFilterPill
                                                        key={ label }
                                                        label={ label }
                                                        value={ value }
                                                        isResettable={ resettable }
                                                        renderItemLabel={ renderTagFilterItemLabel }
                                                        onTextClick={ () => setShowFiltersModal(true) }
                                                        onCloseClick={ handleFilterPillClose }
                                                        onRemoveItem={ handleFilterPillItemRemove }
                                                    />
                                                }
                                            )
                                        }
                                        { appliedFilters && Object.keys(appliedFilters).length > 0 && <button type="button" className="btn btn-none p-0 m-0 fs--1 nowrap" onClick={ () => handleResetFilters() }>Reset all filters</button> }
                                    </div>
                                }
                                <div ref={ pdfRef }>
                                    <PrintPdfHeader organisationLogo={ organisationLogo } organisationName={ organisationName } title="PCNs Issued">
                                        <div className="print-only-filter">
                                            <span className="print-only-filter-label">Date Range</span>
                                            <span className="print-only-filter-value">
                                                { toDateString(fromDate) } - { toDateString(toDate) }
                                            </span>
                                        </div>
                                        <div className="print-only-filter">
                                            <span className="print-only-filter-label">Group By</span>
                                            <span className="print-only-filter-value">
                                                { groupByLabel(pcnsIssuedGroupBy) }
                                            </span>
                                        </div>
                                        <div className="print-only-filter">
                                            <span className="print-only-filter-label">Tags</span>
                                            <span className="print-only-filter-value">
                                                { selectedTagNames }
                                            </span>
                                        </div>
                                    </PrintPdfHeader>
                                {isLoading ? <Loader/> : <>
                                    {reportData && reportData.length > 0 ? <div className="mt-3 d-flex flex-column">
                                        <div className="pl-6 mb-3 print-hide">
                                            <button
                                                className={`btn btn-${chartStyle === "bar" ? "dark" : "secondary"} btn-sm mr-1 nowrap`}
                                                onClick={() => setChartStyle("bar")}><i
                                                className="fas fa-chart-bar mr-2"></i>Bar Graph
                                            </button>
                                            <button
                                                className={`btn btn-${chartStyle === "line" ? "dark" : "secondary"} btn-sm mr-1 ml-1 nowrap`}
                                                onClick={() => setChartStyle("line")}><i
                                                className="fas fa-chart-line mr-2"></i>Line Graph
                                            </button>
                                        </div>
                                        <ReportsCharts data={chartData} chartStyle={chartStyle}/>
                                        <div className="card-body bg-white border-top p-0">
                                            <table
                                                className="table table-sm mb-0 table-striped table-dashboard fs--1 data-table border-bottom border-200 no-footer ">
                                                <thead className="bg-200 text-900">
                                                <tr>
                                                    {reportData && reportData.length > 0 && <>
                                                        <th>{groupingColumnHeading}</th>
                                                        <th className="text-right">PCNs Issued</th>
                                                    </>}
                                                </tr>
                                                </thead>

                                                <tbody>
                                                {!arrayUtilities.isNullOrEmpty(reportData) && reportData.map((item,index) => <tr
                                                    key={index}>
                                                    <td>{item.label}</td>
                                                    <td className="text-right">{stringHelpers.formatDecimal(item.value,0)}</td>
                                                </tr>)}
                                                {totalCount !== null && <tr className="font-weight-bold w-100">
                                                    <td>Total</td>
                                                    <td className="text-right" colSpan={3}>
                                                        {stringHelpers.formatDecimal(totalCount, 0)}
                                                    </td>
                                                </tr>}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div> : <EmptyState message="No Data available for the selected filters"/>}
                                </>}
                                </div>
        {
            showScheduleExportModal &&
            <PcnsIssuedScheduleModal
                tags={ tags || [] }
                organisationId={ organisationId }
                token={ token }
                initialParameters={ scheduleInitialParameters }
                onCancel={ closeScheduleExportModal }
                onSaved={ closeScheduleExportModal }
            />
        }
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {
                showFiltersModal &&
                <PcnsIssuedReportFiltersModal
                    showModal={showFiltersModal}
                    handleClose={() => setShowFiltersModal(false)}
                    handleConfirm={(draftFilters) => {
                        setFromDate(draftFilters.fromDate)
                        setToDate(draftFilters.toDate)
                        setDatePreset(draftFilters.datePreset)
                        setPcnsIssuedGroupBy(draftFilters.pcnsIssuedGroupBy)
                        setSelectedTags(draftFilters.selectedTags)
                        setAppliedFilters(draftFilters.appliedFilters)
                        setShowFilterPills(true)
                        setShowFiltersModal(false)
                        fetchPcnsIssuedReport(draftFilters.fromDate,draftFilters.toDate,draftFilters.pcnsIssuedGroupBy,draftFilters.selectedTags)
                    }}
                    initialFromDate={fromDate}
                    initialToDate={toDate}
                    initialDatePreset={datePreset}
                    initialPcnsIssuedGroupBy={pcnsIssuedGroupBy}
                    initialSelectedTags={selectedTags}
                    initialAppliedFilters={appliedFilters}
                    tags={tags || []}
                    key="pcns-issued-report-filters-modal"
                />
            }
        </>
    )
}
